import React from "react";
import PropTypes from "prop-types";
import { Frame } from "@bitesite/react-figstrap";
import numeral from "numeral";
import RewardCalculatorGraph from "./reward_calculator_graph";
import GiftPreview from "./gift_preview";
import { Text, Colors } from "../../odc-ui";

const ResultsPane = ({
  calculatorMode,
  autoGrowMode,
  printMode,
  inSurgeryMode,
  inDermatologyMode,
  inGoalMode,
  inRewardsMode,
  inHealthcareGoalAutoGrowMode,
  inMonthlyAutoGrowMode,

  campaignInProgress,

  goalBalance,
  treatmentCategory,
  initialBalance,

  ongoingDepositsAmount,
  ongoingDepositsFrequencyOption,

  timeToHitGoalInMonths,

  topUpTotal,
  futureHealthCoinBalance,
  balanceDurationInMonthsOption,

  totalOutOfPocketContributions,
  totalHealthCoinRewardsEarnedExcludingTopUps,
  totalHealthCoinRewardsEarned,

  ongoingClinicPurchasesAmount,
  ongoingClinicPurchasesFrequencyOption,
  rewardRate,
  topUpAmountForInitialBalance,
  topUpAmountForIndividualOngoingDeposit,

  generateGraphData,
}) => {
  return (
    <Frame
      className={`projections ${printMode ? "projections-print-mode" : ""}`}
      vertical
      padding={printMode ? 20 : 40}
      gap={printMode ? 15 : 20}
    >
      <Text type="big-medium" color={Colors.darkGrey100}>
        {inRewardsMode() && "REWARDS PROJECTION"}
        {inGoalMode() &&
          inHealthcareGoalAutoGrowMode() &&
          "HEALTHCARE GOAL PROJECTION"}
        {inGoalMode() &&
          inMonthlyAutoGrowMode() &&
          "MONTHLY AUTO GROW PROJECTION"}
      </Text>

      {inGoalMode() && inHealthcareGoalAutoGrowMode() && (
        <Frame>
          {goalBalance && timeToHitGoalInMonths > 0 ? (
            <div className="regular-bold">
              My Goal is Achieved in approximately {timeToHitGoalInMonths}{" "}
              months when my balance of{" "}
              {numeral(futureHealthCoinBalance(timeToHitGoalInMonths)).format(
                "$0,0.00"
              )}{" "}
              is reached!
            </div>
          ) : (
            <div className="small-medium">
              (fill out your Goal values to see how long it will take you
              achieve your goal)
            </div>
          )}
        </Frame>
      )}

      <Frame gap={6} justifyContent="space-between">
        <div className="label small-medium">My Contribution:</div>
        <Text type="regular-medium" color={Colors.darkGrey100}>
          {numeral(totalOutOfPocketContributions()).format("$0,0.00")}
        </Text>
      </Frame>

      <Frame gap={6} justifyContent="space-between">
        <div className="label small-medium">My Rewards Earned:</div>
        <Text type="regular-medium" color={Colors.darkGrey100}>
          {totalHealthCoinRewardsEarnedExcludingTopUps() > 0
            ? numeral(totalHealthCoinRewardsEarnedExcludingTopUps()).format(
                "$0,0.00"
              )
            : "n/a"}
        </Text>
      </Frame>

      {(printMode || campaignInProgress()) && (
        <Frame horizontal justifyContent="space-between" alignItems="center">
          <div className="label small-medium">My Top-up(s):</div>
          <Text type="regular-medium" color={Colors.darkGrey100}>
            {numeral(topUpTotal()).format("$0,0.00")}
          </Text>
        </Frame>
      )}

      {inGoalMode() && (
        <Frame gap={6} justifyContent="space-between">
          <div className="label small-medium">
            My Total Healthcoin<sup>®</sup> Rewards Earned:
          </div>
          <Text type="regular-medium" color={Colors.darkGrey100}>
            {totalHealthCoinRewardsEarned() > 0
              ? numeral(totalHealthCoinRewardsEarned()).format("$0,0.00")
              : "n/a"}
          </Text>
        </Frame>
      )}

      <Frame gap={6} justifyContent="space-between">
        <div className="label small-medium">My Balance:</div>
        <Text type="regular-medium" color={Colors.darkGrey100}>
          {inHealthcareGoalAutoGrowMode()
            ? numeral(futureHealthCoinBalance(timeToHitGoalInMonths)).format(
                "$0,0.00"
              )
            : numeral(futureHealthCoinBalance()).format("$0,0.00")}
        </Text>
      </Frame>

      <div>
        <div className="divider" />
      </div>

      {inGoalMode() && (
        <RewardCalculatorGraph
          title={
            <span>
              Total Healthcoin<sup>®</sup> Balance Over Time
            </span>
          }
          data={generateGraphData()}
          animated={!printMode}
        />
      )}

      {inRewardsMode() && inDermatologyMode() && (
        <GiftPreview initialBalance={initialBalance} />
      )}

      {!printMode && (
        <Frame>
          <a
            href={`/print_calculator_results?default_calculator_mode=${calculatorMode}&default_treatment_category=${treatmentCategory}&default_auto_grow_mode=${autoGrowMode}&default_initial_balance=${initialBalance}&default_goal_balance=${goalBalance}&default_balance_duration_in_months=${
              balanceDurationInMonthsOption
                ? balanceDurationInMonthsOption.value
                : 0
            }&default_ongoing_deposits_amount=${ongoingDepositsAmount}&default_ongoing_deposits_frequency=${
              ongoingDepositsFrequencyOption.value
            }&default_ongoing_clinic_purchases_amount=${ongoingClinicPurchasesAmount}&default_ongoing_clinic_purchases_frequency=${
              ongoingClinicPurchasesFrequencyOption.value
            }&default_reward_rate=${rewardRate}&default_top_up_amount_for_initial_balance=${topUpAmountForInitialBalance}&default_top_up_amount_for_individual_ongoing_deposit=${topUpAmountForIndividualOngoingDeposit}&default_time_to_hit_goal_in_months=${timeToHitGoalInMonths}`}
            target="_blank"
            rel="noreferrer"
          >
            Print your results to PDF
          </a>
        </Frame>
      )}

      <Frame vertical gap={20}>
        {inRewardsMode() && (
          <div className="you-could-use small-medium">
            Based on your input, you would earn a Healthcoin<sup>®</sup> reward
            of{" "}
            {totalHealthCoinRewardsEarned() > 0
              ? numeral(totalHealthCoinRewardsEarned()).format("$0,0.00")
              : "n/a"}{" "}
            (at a rate of {rewardRate}%
            {(printMode || campaignInProgress()) &&
              ` including ${numeral(topUpTotal()).format("$0,0.00")} top-up`}
            ) for investing{" "}
            {`${
              initialBalance ? numeral(initialBalance).format("$0,0.00") : "$0"
            }`}{" "}
            over{" "}
            {`${
              balanceDurationInMonthsOption &&
              balanceDurationInMonthsOption.label
                ? balanceDurationInMonthsOption.label
                : "0 months"
            }`}
            . Plus earn {rewardRate}% rewards on any outstanding balance.
          </div>
        )}
        <div className="you-could-use small-medium">
          Use your Healthcoin<sup>®</sup> rewards earned to pay for clinic
          services and products!{" "}
        </div>
        <div className="disclaimer extra-small-medium">
          Disclaimer: This rewards calculator is meant for informational
          purposes only and gives an overall idea of the values calculated. All
          calculated values are based on a number of assumptions and actual
          values may differ.
        </div>
      </Frame>
    </Frame>
  );
};

ResultsPane.propTypes = {
  calculatorMode: PropTypes.string.isRequired,
  autoGrowMode: PropTypes.string.isRequired,
  printMode: PropTypes.bool,
  inDermatologyMode: PropTypes.func.isRequired,
  inSurgeryMode: PropTypes.func.isRequired,
  inGoalMode: PropTypes.func.isRequired,
  inRewardsMode: PropTypes.func.isRequired,
  inHealthcareGoalAutoGrowMode: PropTypes.func.isRequired,
  inMonthlyAutoGrowMode: PropTypes.func.isRequired,

  campaignInProgress: PropTypes.func.isRequired,

  goalBalance: PropTypes.number,

  treatmentCategory: PropTypes.string.isRequired,

  initialBalance: PropTypes.number,

  ongoingDepositsAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  ongoingDepositsFrequencyOption: PropTypes.object.isRequired,

  ongoingClinicPurchasesAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  ongoingClinicPurchasesFrequencyOption: PropTypes.object.isRequired,

  balanceDurationInMonthsOption: PropTypes.object,

  topUpTotal: PropTypes.func.isRequired,
  futureHealthCoinBalance: PropTypes.func.isRequired,
  timeToHitGoalInMonths: PropTypes.number.isRequired,

  totalOutOfPocketContributions: PropTypes.func.isRequired,
  totalHealthCoinRewardsEarnedExcludingTopUps: PropTypes.func.isRequired,
  totalHealthCoinRewardsEarned: PropTypes.func.isRequired,

  rewardRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  topUpAmountForInitialBalance: PropTypes.number.isRequired,
  topUpAmountForIndividualOngoingDeposit: PropTypes.number.isRequired,

  generateGraphData: PropTypes.func.isRequired,
};

export default ResultsPane;
